import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Sidebar } from '@/components/Sidebar';
import { DoorOpen, TrendingUp, Users, XCircle, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { formatMoney } from '@/lib/currency';

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('RUB');
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    setLoading(true);
    Promise.all([
      api.get(`/analytics?month=${month}`),
      api.get('/settings'),
    ])
      .then(([{ data: d }, { data: settings }]) => {
        setData(d);
        setCurrency(settings?.currency || 'RUB');
      })
      .catch(err => { if (err.response?.status === 403) navigate('/dashboard'); })
      .finally(() => setLoading(false));
  }, [month, navigate]);

  const MONTHS_RU = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  const monthLabel = month ? `${MONTHS_RU[parseInt(month.split('-')[1]) - 1]} ${month.split('-')[0]}` : '';

  return (
    <div data-testid="analytics-page" className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8 animate-fade-in">
            <h1 className="font-heading text-2xl lg:text-3xl text-stone-200">Аналитика</h1>
            <div className="flex items-center gap-3">
              <Input type="month" value={month} onChange={e => setMonth(e.target.value)}
                className="bg-stone-950/50 border-stone-800 text-stone-200 w-44 h-9 text-sm" />
              <Badge className="bg-stone-800 text-stone-300 border-stone-700 text-xs">{monthLabel}</Badge>
            </div>
          </div>

          {loading ? (
            <p className="text-stone-500 text-center py-12">Загрузка аналитики...</p>
          ) : !data ? (
            <p className="text-stone-600 text-center py-12">Нет данных</p>
          ) : (
            <div className="space-y-6 animate-fade-in delay-1">

              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Всего записей', value: data.total_appointments, icon: Users, color: 'text-stone-300' },
                  { label: 'Завершено', value: data.funnel?.completed || 0, icon: CheckCircle, color: 'text-green-400' },
                  { label: 'Отменено', value: data.funnel?.cancelled || 0, icon: XCircle, color: 'text-red-400' },
                  { label: 'Доходимость', value: `${data.funnel?.completion_rate || 0}%`, icon: TrendingUp, color: 'text-amber-400' },
                ].map((c, i) => (
                  <Card key={i} className="bg-stone-900 border-stone-800">
                    <CardContent className="p-4 flex items-center gap-3">
                      <c.icon size={20} className={c.color} />
                      <div>
                        <p className="text-stone-500 text-[10px] uppercase">{c.label}</p>
                        <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Room Utilization */}
              <Card className="bg-stone-900 border-stone-800">
                <CardHeader>
                  <CardTitle className="text-stone-200 font-heading text-lg flex items-center gap-2">
                    <DoorOpen size={18} className="text-teal-400" /> Загрузка кабинетов
                  </CardTitle>
                  <p className="text-stone-500 text-xs">{data.working_days} рабочих дней, 8ч/день на кабинет</p>
                </CardHeader>
                <CardContent>
                  {(data.room_utilization || []).length === 0 ? (
                    <p className="text-stone-600 text-sm text-center py-4">Нет кабинетов</p>
                  ) : (
                    <div className="space-y-3">
                      {data.room_utilization.map(r => (
                        <div key={r.room_id} className="p-3 rounded-lg bg-stone-950/30 border border-stone-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-stone-200 text-sm font-medium">{r.room_name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-stone-500 text-xs">{r.appointments} записей</span>
                              <span className={`text-sm font-bold ${r.utilization_pct > 70 ? 'text-green-400' : r.utilization_pct > 30 ? 'text-amber-400' : 'text-red-400'}`}>
                                {r.utilization_pct}%
                              </span>
                            </div>
                          </div>
                          <div className="h-2 rounded-full bg-stone-800 overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{
                              width: `${Math.min(r.utilization_pct, 100)}%`,
                              background: r.utilization_pct > 70 ? '#22c55e' : r.utilization_pct > 30 ? '#f59e0b' : '#ef4444',
                            }} />
                          </div>
                          <p className="text-stone-600 text-[10px] mt-1">
                            {Math.round(r.booked_minutes / 60)}ч занято / {Math.round(r.max_minutes / 60)}ч макс.
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Doctor revenue */}
              <Card className="bg-stone-900 border-stone-800">
                <CardHeader>
                  <CardTitle className="text-stone-200 font-heading text-lg flex items-center gap-2">
                    <TrendingUp size={18} className="text-amber-400" /> Выручка специалистов
                  </CardTitle>
                  <p className="text-stone-500 text-xs">Суммы в разных валютах показаны отдельно и не складываются.</p>
                </CardHeader>
                <CardContent>
                  {(data.revenue_ranking || []).length === 0 ? (
                    <p className="text-stone-600 text-sm text-center py-4">Нет оплаченных приёмов за период</p>
                  ) : (
                    <div className="space-y-2">
                      {data.revenue_ranking.map((d) => {
                        const revenueItems = (d.revenue_by_currency || []).filter(item => Number(item.amount || 0) !== 0);
                        return (
                          <div key={d.doctor_id} className="flex items-center gap-3 p-3 rounded-lg bg-stone-950/30 border border-stone-800/50">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-stone-200 text-sm font-medium truncate">{d.doctor_name}</span>
                                <div className="text-right text-amber-400 text-sm font-bold">
                                  {(revenueItems.length > 0 ? revenueItems : [{ currency: data.currency || currency, amount: 0 }]).map(item => (
                                    <div key={item.currency}>{formatMoney(item.amount, item.currency)}</div>
                                  ))}
                                </div>
                              </div>
                              <p className="text-stone-600 text-[10px] mt-1">{d.appointments} оплаченных приёмов</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Cancellation Funnel */}
              <Card className="bg-stone-900 border-stone-800">
                <CardHeader>
                  <CardTitle className="text-stone-200 font-heading text-lg flex items-center gap-2">
                    <AlertTriangle size={18} className="text-red-400" /> Воронка доходимости
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.total_appointments === 0 ? (
                    <p className="text-stone-600 text-sm text-center py-4">Нет данных за период</p>
                  ) : (
                    <div className="space-y-4">
                      {/* Visual funnel */}
                      <div className="space-y-1">
                        {[
                          { label: 'Забронировано', value: data.funnel.total_booked, color: 'bg-sky-600', pct: 100 },
                          { label: 'Подтверждено', value: data.funnel.confirmed, color: 'bg-blue-600', pct: data.funnel.confirmed / data.funnel.total_booked * 100 },
                          { label: 'Завершено', value: data.funnel.completed, color: 'bg-green-600', pct: data.funnel.completed / data.funnel.total_booked * 100 },
                        ].map((s, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-stone-400 text-xs w-28 text-right shrink-0">{s.label}</span>
                            <div className="flex-1 h-8 rounded-md bg-stone-800/50 overflow-hidden relative">
                              <div className={`h-full ${s.color} rounded-md transition-all flex items-center px-3`} style={{ width: `${Math.max(s.pct, 2)}%` }}>
                                <span className="text-white text-xs font-bold">{s.value}</span>
                              </div>
                            </div>
                            <span className="text-stone-500 text-xs w-12">{Math.round(s.pct)}%</span>
                          </div>
                        ))}
                      </div>

                      {/* Stats grid */}
                      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-stone-800">
                        <div className="text-center p-3 rounded-lg bg-red-900/10 border border-red-900/20">
                          <p className="text-red-400 text-lg font-bold">{data.funnel.cancelled}</p>
                          <p className="text-stone-500 text-[10px] uppercase">Отменено</p>
                          <p className="text-stone-600 text-[10px]">{data.funnel.cancel_rate}%</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-amber-900/10 border border-amber-900/20">
                          <p className="text-amber-400 text-lg font-bold">{data.funnel.late_cancels}</p>
                          <p className="text-stone-500 text-[10px] uppercase">Поздние отмены</p>
                          <p className="text-stone-600 text-[10px]">&lt; 24ч до приёма</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-violet-900/10 border border-violet-900/20">
                          <p className="text-violet-400 text-lg font-bold">{data.funnel.no_show}</p>
                          <p className="text-stone-500 text-[10px] uppercase">Неявки</p>
                          <p className="text-stone-600 text-[10px]">no-show</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
